import {
    applyFurnizorPricingToRoot,
    builderFormToCalculatorInput,
    calculateProductPricing,
    createDefaultFurnizorPricingForm,
    createDefaultProductBuilderForm,
    createFurnizorPricingClientKey,
    DEFAULT_PRICING_SETTINGS,
    formRatioFieldsToPricingDetail,
    formatNumericInputValue,
    getDefaultFurnizorPricing,
    hydrateCostUpliftsFromStored,
    hydrateDiscountNotesFromLegacy,
    hydrateDiscountsFromLegacy,
    hydrateSystemsFromPricingDetail,
    mergeFormWithFurnizorPricing,
    parseNumericInput,
    PERCENTAGE_RATIO_FORM_FIELDS,
    persistActiveFurnizorPricingFromRoot,
    ratioToPercentageInput,
    roundMoney,
    parseStoredDiscountNotes,
    serializeCostUpliftsForPayload,
    serializeStoredDiscountNotes,
    syncDiscountsListToLegacy,
    syncSystemsListToLegacy,
    systemsArrayForPricingDetail,
    systemsArrayFromForm,
} from '@/utils/priceListProductCalculator';
import {
    cloneTransportCostBreakdown,
    serializeTransportCostBreakdown,
} from '@/utils/transportCostBreakdown';
import {
    cloneStampDutyBreakdown,
    normalizeStampDutyCurrency,
    serializeStampDutyBreakdown,
} from '@/utils/stampDutyBreakdown';
import {
    buildCategoryAssignmentsFromSystems,
    hydrateSystemSubcategories,
    normalizeSystemValue,
} from '@/data/productCategories';
import { getChapterName, getSubchapterName } from '@/data/productStructureLookups';
import { parseProductCodeStructure, findManufacturerByCode } from '@/utils/productStructureHelpers';
import { getManufacturerCodes } from '@/data/manufacturerCodes';

function toInputValue(value) {
    if (value === null || value === undefined) {
        return '';
    }
    return String(value);
}

function toPriceInputValue(value, decimals = 2) {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    return formatNumericInputValue(value, decimals);
}

/**
 * Persist money fields as plain numbers (never ro-RO display strings like
 * "1.234,56" / "1.234"). Those strings break Number()/backend parse when
 * re-loaded after curs save → lista în lucru / lista curentă.
 * @param {unknown} value
 * @param {number} [decimals]
 * @returns {number|null}
 */
function toPayloadMoney(value, decimals = 4) {
    if (value === null || value === undefined || String(value).trim() === '') {
        return null;
    }
    const numeric = parseNumericInput(value);
    if (!Number.isFinite(numeric)) {
        return null;
    }
    return roundMoney(numeric, decimals);
}

function toBooleanValue(value) {
    if (typeof value === 'boolean') {
        return value;
    }
    if (value === null || value === undefined || value === '') {
        return false;
    }
    if (typeof value === 'number') {
        return value !== 0;
    }
    const normalized = String(value).trim().toLowerCase();
    return normalized !== '' && normalized !== '0' && normalized !== 'false' && normalized !== 'no';
}

/**
 * Maps one API/flat furnizor pricing blob into a builder tab row (percentage form values).
 * @param {Record<string, unknown>} source
 * @param {{ isDefault?: boolean, fallbackPrice?: unknown }} [options]
 * @returns {Record<string, unknown>}
 */
function mapSourceToFurnizorPricingForm(source = {}, options = {}) {
    const pricing = createDefaultFurnizorPricingForm({
        id: source.id ?? null,
        clientKey: source.clientKey || createFurnizorPricingClientKey(),
        furnizorId: source.furnizorId ?? null,
        furnizorCode: toInputValue(source.furnizorCode),
        furnizorName: toInputValue(source.furnizorName),
        usesManufacturerAsFurnizor: source.usesManufacturerAsFurnizor
            ?? !(source.furnizorId || source.furnizorCode),
        isDefault: options.isDefault ?? Boolean(source.isDefault),
    });

    pricing.supplierListPriceOld = toPriceInputValue(source.supplierListPriceOld, 4);
    pricing.supplierListPriceNew = toPriceInputValue(source.supplierListPriceNew, 4);
    pricing.currency = toInputValue(source.currency || 'ron');
    pricing.paymentTermDays = toInputValue(source.paymentTermDays);
    PERCENTAGE_RATIO_FORM_FIELDS.forEach((field) => {
        if (field === 'priceAdjustmentRatio') {
            return;
        }
        pricing[field] = ratioToPercentageInput(source[field]);
    });
    pricing.transportCostEstimated = toPriceInputValue(source.transportCostEstimated, 2);
    pricing.listPriceRonIncVatReference = toPriceInputValue(source.listPriceRonIncVatReference, 2);
    if (!pricing.listPriceRonIncVatReference && options.fallbackPrice) {
        pricing.listPriceRonIncVatReference = toPriceInputValue(options.fallbackPrice, 2);
    }
    pricing.lockMarkupToReference = Boolean(source.lockMarkupToReference);
    pricing.overrideListPriceRonIncVatFinal = Boolean(source.overrideListPriceRonIncVatFinal);
    pricing.overrideListPriceRonIncVatFinalValue = toPriceInputValue(
        source.overrideListPriceRonIncVatFinalValue,
        2,
    );
    pricing.notes = toInputValue(source.notes);
    pricing.discountNotes = parseStoredDiscountNotes(source.discountNotes);
    pricing.manufacturerProductCode = toInputValue(source.manufacturerProductCode);
    pricing.manufacturerProductName = toInputValue(source.manufacturerProductName);
    hydrateCostUpliftsFromStored(pricing, source.costUplifts);
    pricing.stampDutyAmount = toPriceInputValue(source.stampDutyAmount, 2) || '0';
    pricing.stampDutyCurrency = normalizeStampDutyCurrency(
        source.stampDutyCurrency || 'ron',
    );
    pricing.stampDutyBreakdown = cloneStampDutyBreakdown(source.stampDutyBreakdown);
    pricing.transportCostBreakdown = cloneTransportCostBreakdown(
        source.transportCostBreakdown,
    );
    // Keep stored override as-is. Clearing it when a reference price exists made
    // open→save of unrelated fields (e.g. denumire furnizor) drop the override
    // and rewrite list price by a rounding cent.
    pricing.priceAdjustmentRatio = '';
    hydrateDiscountsFromLegacy(pricing);
    hydrateDiscountNotesFromLegacy(pricing);
    return pricing;
}

/**
 * @param {Record<string, unknown>} pricingDetail
 * @param {Record<string, unknown>} item
 * @returns {Record<string, unknown>[]}
 */
function buildFurnizorPricingsFromDetail(pricingDetail, item = {}) {
    const rows = Array.isArray(pricingDetail.furnizorPricings)
        ? pricingDetail.furnizorPricings
        : [];
    if (rows.length) {
        const hasDefault = rows.some((row) => row.isDefault);
        return rows.map((row, index) => mapSourceToFurnizorPricingForm(
            mergeFurnizorSourceWithFlatDetail(row, pricingDetail),
            {
                isDefault: hasDefault ? Boolean(row.isDefault) : index === 0,
                fallbackPrice: item.price,
            },
        ));
    }
    return [mapSourceToFurnizorPricingForm(pricingDetail, {
        isDefault: true,
        fallbackPrice: item.price,
    })];
}

/**
 * Prefer explicit furnizor-row values; fill blanks from flat pricing_detail compat fields.
 * @param {Record<string, unknown>} row
 * @param {Record<string, unknown>} pricingDetail
 * @returns {Record<string, unknown>}
 */
function mergeFurnizorSourceWithFlatDetail(row, pricingDetail) {
    const merged = { ...(pricingDetail || {}), ...(row || {}) };
    const preferRowKeys = [
        'id',
        'clientKey',
        'furnizorId',
        'furnizorCode',
        'furnizorName',
        'usesManufacturerAsFurnizor',
        'isDefault',
    ];
    for (const key of preferRowKeys) {
        if (row && Object.prototype.hasOwnProperty.call(row, key)) {
            merged[key] = row[key];
        }
    }
    // For calc fields, keep row value when present; otherwise keep flat detail value.
    for (const field of PERCENTAGE_RATIO_FORM_FIELDS) {
        if (row?.[field] !== undefined && row?.[field] !== null && row?.[field] !== '') {
            merged[field] = row[field];
        } else if (
            pricingDetail?.[field] !== undefined
            && pricingDetail?.[field] !== null
            && pricingDetail?.[field] !== ''
        ) {
            merged[field] = pricingDetail[field];
        }
    }
    for (const field of [
        'manufacturerProductCode',
        'manufacturerProductName',
        'supplierListPriceOld',
        'supplierListPriceNew',
        'currency',
        'paymentTermDays',
        'transportCostEstimated',
        'transportCostBreakdown',
        'stampDutyAmount',
        'stampDutyCurrency',
        'stampDutyBreakdown',
        'listPriceRonIncVatReference',
        'lockMarkupToReference',
        'overrideListPriceRonIncVatFinal',
        'overrideListPriceRonIncVatFinalValue',
        'listPriceRonIncVatFinal',
        'notes',
        'discountNotes',
        'costUplifts',
        'pricingSettingsSnapshot',
    ]) {
        if (row?.[field] !== undefined && row?.[field] !== null && row?.[field] !== '') {
            merged[field] = row[field];
        } else if (
            pricingDetail?.[field] !== undefined
            && pricingDetail?.[field] !== null
            && pricingDetail?.[field] !== ''
        ) {
            merged[field] = pricingDetail[field];
        }
    }
    return merged;
}

/**
 * @param {Record<string, unknown>} pricing
 * @param {Record<string, unknown>} settings
 * @param {Record<string, unknown>|null} computedForRow
 * @returns {Record<string, unknown>}
 */
function markupActualRatioForPayload(pricing, ratioFields) {
    const raw = pricing?.markupActual;
    if (raw === null || raw === undefined || String(raw).trim() === '') {
        return null;
    }
    return roundMoney(ratioFields.markupActual, 6);
}

function computePricingForForm(form, settings) {
    return calculateProductPricing(
        builderFormToCalculatorInput(form),
        settings,
    );
}

function furnizorPricingFormToPayload(pricing, settings, computedForRow = null) {
    const row = { ...pricing };
    syncDiscountsListToLegacy(row);
    const ratioFields = formRatioFieldsToPricingDetail(row);
    return {
        id: pricing.id || undefined,
        clientKey: pricing.clientKey,
        furnizorId: pricing.furnizorId || null,
        furnizorCode: pricing.furnizorCode || '',
        furnizorName: pricing.furnizorName || '',
        usesManufacturerAsFurnizor: Boolean(pricing.usesManufacturerAsFurnizor),
        isDefault: Boolean(pricing.isDefault),
        manufacturerProductCode: toInputValue(pricing.manufacturerProductCode),
        manufacturerProductName: toInputValue(pricing.manufacturerProductName),
        supplierListPriceOld: toPayloadMoney(pricing.supplierListPriceOld, 4),
        supplierListPriceNew: toPayloadMoney(pricing.supplierListPriceNew, 4),
        currency: pricing.currency,
        paymentTermDays: pricing.paymentTermDays,
        discount1: ratioFields.discount1,
        discount2: ratioFields.discount2,
        cashDiscount: ratioFields.cashDiscount,
        marketingDiscount: ratioFields.marketingDiscount,
        transportCostEstimated: toPayloadMoney(pricing.transportCostEstimated, 2),
        transportCostRate: ratioFields.transportCostRate,
        transportCostBreakdown: serializeTransportCostBreakdown(
            pricing.transportCostBreakdown,
        ),
        stampDutyAmount: parseNumericInput(pricing.stampDutyAmount) > 0
            ? parseNumericInput(pricing.stampDutyAmount)
            : 0,
        stampDutyCurrency: normalizeStampDutyCurrency(
            pricing.stampDutyCurrency || 'ron',
        ),
        stampDutyBreakdown: serializeStampDutyBreakdown(pricing.stampDutyBreakdown),
        markupReference: ratioFields.markupReference,
        markupActual: markupActualRatioForPayload(pricing, ratioFields),
        lockMarkupToReference: Boolean(pricing.lockMarkupToReference),
        listPriceRonIncVatReference: toPayloadMoney(
            pricing.listPriceRonIncVatReference,
            2,
        ),
        priceAdjustmentRatio: ratioFields.priceAdjustmentRatio,
        overrideListPriceRonIncVatFinal: pricing.overrideListPriceRonIncVatFinal,
        overrideListPriceRonIncVatFinalValue: toPayloadMoney(
            pricing.overrideListPriceRonIncVatFinalValue,
            2,
        ),
        notes: toInputValue(pricing.notes),
        discountNotes: serializeStoredDiscountNotes(pricing.discountNotes),
        costUplifts: serializeCostUpliftsForPayload(
            pricing.costUplifts,
            pricing.costUpliftNotes,
            pricing.costUpliftTypes,
        ),
        listPriceRonIncVatFinal: toPayloadMoney(
            computedForRow?.listPriceRonIncVatFinal
                ?? pricing.listPriceRonIncVatFinal
                ?? null,
            2,
        ),
        pricingSettingsSnapshot: { ...settings },
    };
}

export function pricingDetailToBuilderForm(pricingDetail, item = {}) {
    const form = createDefaultProductBuilderForm();

    if (!pricingDetail) {
        return {
            productBuilderForm: form,
            pricingSettings: { ...DEFAULT_PRICING_SETTINGS },
        };
    }

    form.system1 = toInputValue(pricingDetail.system1);
    form.system2 = toInputValue(pricingDetail.system2);
    form.system3 = toInputValue(pricingDetail.system3);
    form.chapterCode = toInputValue(pricingDetail.chapterCode);
    form.chapterName = toInputValue(pricingDetail.chapterName);
    form.subchapterCode = toInputValue(pricingDetail.subchapterCode);
    form.subchapterName = toInputValue(pricingDetail.subchapterName);
    form.progressiveNumber = toInputValue(pricingDetail.progressiveNumber);
    form.manufacturerName = toInputValue(pricingDetail.manufacturerName);
    form.manufacturerCode = toInputValue(pricingDetail.manufacturerCode);
    form.serialNumber = toBooleanValue(pricingDetail.serialNumber);
    form.productName = toInputValue(item.product_name || item.name);
    form.measurementUnit = toInputValue(item.measurement_unit || form.measurementUnit);
    form.weight = toInputValue(pricingDetail.weight);
    form.nomenclatureCode = toInputValue(pricingDetail.nomenclatureCode);
    form.productNotes = toInputValue(pricingDetail.productNotes);
    form.commercialPolicyDiscount = ratioToPercentageInput(
        pricingDetail.commercialPolicyDiscount,
    );
    form.isCompositeProduct = Boolean(pricingDetail.isCompositeProduct);
    form.componentProductCodes = Array.isArray(pricingDetail.componentProductCodes)
        ? [...pricingDetail.componentProductCodes]
        : [];
    form.componentProductsKeepInList = Array.isArray(pricingDetail.componentProductsKeepInList)
        ? [...pricingDetail.componentProductsKeepInList]
        : [...form.componentProductCodes];
    form.visibleInOfferApp = item.visible_in_offer_app ?? pricingDetail.visibleInOfferApp ?? true;
    form.status = item.status ?? pricingDetail.status ?? null;
    form.overrideProductCode = Boolean(pricingDetail.overrideProductCode);
    form.overrideProductCodeValue = toInputValue(pricingDetail.overrideProductCodeValue);

    form.furnizorPricings = buildFurnizorPricingsFromDetail(pricingDetail, item);
    const defaultPricing = getDefaultFurnizorPricing(form) || form.furnizorPricings[0];
    applyFurnizorPricingToRoot(form, defaultPricing);

    const snapshot = defaultPricing
        ? (pricingDetail.pricingSettingsSnapshot
            || form.furnizorPricings.find((row) => row.isDefault)?.pricingSettingsSnapshot
            || {})
        : (pricingDetail.pricingSettingsSnapshot || {});
    // Prefer snapshot from flattened default on pricing_detail (compat).
    const settingsSource = pricingDetail.pricingSettingsSnapshot || snapshot;
    const pricingSettings = {
        exchangeRateEur: settingsSource.exchangeRateEur ?? DEFAULT_PRICING_SETTINGS.exchangeRateEur,
        exchangeRatePln: settingsSource.exchangeRatePln
            ?? settingsSource.exchangeRateRon
            ?? DEFAULT_PRICING_SETTINGS.exchangeRatePln,
        exchangeRateUsd: settingsSource.exchangeRateUsd ?? DEFAULT_PRICING_SETTINGS.exchangeRateUsd,
        vatRate: settingsSource.vatRate ?? DEFAULT_PRICING_SETTINGS.vatRate,
    };

    hydrateSystemsFromPricingDetail(form, pricingDetail, item.category_assignments || []);
    hydrateSystemSubcategories(form, item.category_assignments || []);

    return { productBuilderForm: form, pricingSettings };
}

/**
 * Pre-fills the builder for an existing product that has no pricing_detail yet.
 * Keeps the current product code and price as reference; user completes calculator steps.
 * @param {Object} item
 * @param {string[]} [categories]
 * @returns {{ productBuilderForm: Object, pricingSettings: Object }}
 */
export function legacyProductToBuilderForm(item, categories = []) {
    const { productBuilderForm, pricingSettings } = pricingDetailToBuilderForm(null, item);
    const form = productBuilderForm;

    form.productName = toInputValue(item.product_name || item.name);
    form.manufacturerProductName = toInputValue(
        item?.pricing_detail?.manufacturerProductName
        || item?.manufacturer_product_name
        || ''
    );
    form.measurementUnit = toInputValue(item.measurement_unit || form.measurementUnit);
    form.visibleInOfferApp = item.visible_in_offer_app ?? form.visibleInOfferApp ?? true;
    form.status = item.status ?? form.status ?? null;
    form.categories = [...(categories || [])];

    const structure = parseProductCodeStructure(item.product_code);
    if (structure) {
        form.chapterCode = structure.chapterCode;
        form.subchapterCode = structure.subchapterCode;
        form.progressiveNumber = structure.progressiveNumber;

        const manufacturer = findManufacturerByCode(getManufacturerCodes(), structure.manufacturerCode);
        if (manufacturer) {
            form.manufacturerCode = manufacturer.code;
            form.manufacturerName = manufacturer.name;
        } else if (structure.manufacturerCode) {
            form.manufacturerCode = structure.manufacturerCode;
        }
    }

    if (categories?.length) {
        form.systems = categories.map((category) => normalizeSystemValue(category)).filter(Boolean);
        if (!form.systems.length) {
            form.systems = [''];
        }
        syncSystemsListToLegacy(form);
        if (form.chapterCode) {
            form.chapterName = getChapterName(form.system1, form.chapterCode);
        }
        if (form.subchapterCode) {
            form.subchapterName = getSubchapterName(
                form.system1,
                form.chapterCode,
                form.subchapterCode,
            );
        }
    }

    form.overrideProductCode = true;
    form.overrideProductCodeValue = toInputValue(item.product_code);

    // The current list price is the reference (AF). Majorare (AG) stays 0 until explicitly set.
    const currentPrice = item.price !== null && item.price !== undefined && item.price !== ''
        ? toPriceInputValue(item.price, 2)
        : '';
    form.listPriceRonIncVatReference = currentPrice;
    form.priceAdjustmentRatio = '';
    form.manualListPriceRonIncVatFinal = '';
    form.useManualFinalPrice = false;

    hydrateSystemSubcategories(form, item.category_assignments || []);
    persistActiveFurnizorPricingFromRoot(form);

    return { productBuilderForm: form, pricingSettings };
}

/**
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>} settings
 * @param {Record<string, unknown>} computedProduct
 * @param {Record<string, unknown>|null} [defaultComputedProduct]
 */
export function builderFormToPricingDetailPayload(
    form,
    settings,
    computedProduct,
    defaultComputedProduct = null,
) {
    persistActiveFurnizorPricingFromRoot(form);
    syncSystemsListToLegacy(form);
    syncDiscountsListToLegacy(form);
    const ratioFields = formRatioFieldsToPricingDetail(form);
    const systems = systemsArrayForPricingDetail(form);
    const systemSubcategories = Array.isArray(form.systemSubcategories)
        ? form.systemSubcategories.map((value) => String(value ?? '').trim())
        : [];

    const defaultPricing = getDefaultFurnizorPricing(form);
    let defaultCalc = defaultComputedProduct || computedProduct;
    const furnizorPricings = (form.furnizorPricings || []).map((pricing) => {
        const computedForRow = computePricingForForm(
            mergeFormWithFurnizorPricing(form, pricing),
            settings,
        );
        if (pricing.isDefault) {
            defaultCalc = computedForRow;
        }
        return furnizorPricingFormToPayload(pricing, settings, computedForRow);
    });

    // Flat calc keys = default furnizor (API compat for older readers).
    const flatSource = defaultPricing || form;
    const flatRatios = defaultPricing
        ? formRatioFieldsToPricingDetail(defaultPricing)
        : ratioFields;

    return {
        system1: form.system1,
        system2: form.system2,
        system3: form.system3,
        systems,
        systemSubcategories: systems.map((_, index) => systemSubcategories[index] || ''),
        chapterCode: form.chapterCode,
        chapterName: form.chapterName,
        subchapterCode: form.subchapterCode,
        subchapterName: form.subchapterName,
        progressiveNumber: form.progressiveNumber,
        manufacturerName: form.manufacturerName,
        manufacturerCode: form.manufacturerCode,
        serialNumber: Boolean(form.serialNumber),
        manufacturerProductCode: toInputValue(flatSource.manufacturerProductCode),
        manufacturerProductName: toInputValue(flatSource.manufacturerProductName),
        supplierListPriceOld: toPayloadMoney(flatSource.supplierListPriceOld, 4),
        supplierListPriceNew: toPayloadMoney(flatSource.supplierListPriceNew, 4),
        currency: flatSource.currency,
        paymentTermDays: flatSource.paymentTermDays,
        discount1: flatRatios.discount1,
        discount2: flatRatios.discount2,
        cashDiscount: flatRatios.cashDiscount,
        marketingDiscount: flatRatios.marketingDiscount,
        transportCostEstimated: toPayloadMoney(flatSource.transportCostEstimated, 2),
        transportCostRate: flatRatios.transportCostRate,
        transportCostBreakdown: serializeTransportCostBreakdown(
            flatSource.transportCostBreakdown,
        ),
        stampDutyAmount: parseNumericInput(flatSource.stampDutyAmount) > 0
            ? parseNumericInput(flatSource.stampDutyAmount)
            : 0,
        stampDutyCurrency: normalizeStampDutyCurrency(
            flatSource.stampDutyCurrency || 'ron',
        ),
        stampDutyBreakdown: serializeStampDutyBreakdown(flatSource.stampDutyBreakdown),
        markupReference: flatRatios.markupReference,
        markupActual: markupActualRatioForPayload(flatSource, flatRatios),
        lockMarkupToReference: Boolean(flatSource.lockMarkupToReference),
        listPriceRonIncVatReference: toPayloadMoney(
            flatSource.listPriceRonIncVatReference,
            2,
        ),
        priceAdjustmentRatio: flatRatios.priceAdjustmentRatio,
        weight: form.weight,
        nomenclatureCode: form.nomenclatureCode,
        productNotes: toInputValue(form.productNotes),
        commercialPolicyDiscount: (() => {
            const percent = parseNumericInput(form.commercialPolicyDiscount);
            if (!Number.isFinite(percent) || form.commercialPolicyDiscount === ''
                || form.commercialPolicyDiscount == null) {
                return null;
            }
            return percent / 100;
        })(),
        isCompositeProduct: form.isCompositeProduct,
        componentProductCodes: Array.isArray(form.componentProductCodes)
            ? [...form.componentProductCodes]
            : [],
        componentProductsKeepInList: Array.isArray(form.componentProductsKeepInList)
            ? [...form.componentProductsKeepInList]
            : [],
        visibleInOfferApp: form.visibleInOfferApp,
        overrideProductCode: form.overrideProductCode,
        overrideProductCodeValue: form.overrideProductCodeValue,
        overrideListPriceRonIncVatFinal: flatSource.overrideListPriceRonIncVatFinal,
        overrideListPriceRonIncVatFinalValue: toPayloadMoney(
            flatSource.overrideListPriceRonIncVatFinalValue,
            2,
        ),
        notes: toInputValue(flatSource.notes),
        discountNotes: serializeStoredDiscountNotes(flatSource.discountNotes),
        costUplifts: serializeCostUpliftsForPayload(
            flatSource.costUplifts,
            flatSource.costUpliftNotes,
            flatSource.costUpliftTypes,
        ),
        listPriceRonIncVatFinal: toPayloadMoney(defaultCalc?.listPriceRonIncVatFinal, 2),
        pricingSettingsSnapshot: { ...settings },
        furnizorPricings,
        defaultFurnizorPricingId: defaultPricing?.id || null,
    };
}

export function builderFormToSavePayload({
    form,
    settings,
    computedProduct,
    categories,
    categoryAssignments,
    priceListIds,
    oldProductCode = null,
}) {
    persistActiveFurnizorPricingFromRoot(form);
    const defaultPricing = getDefaultFurnizorPricing(form);
    const priceSource = computePricingForForm(
        mergeFormWithFurnizorPricing(form, defaultPricing),
        settings,
    );
    const resolvedCategoryAssignments = categoryAssignments?.length
        ? categoryAssignments
        : buildCategoryAssignmentsFromSystems(
            Array.isArray(form.systems) ? form.systems : systemsArrayFromForm(form),
            form.systemSubcategories || [],
        );
    const resolvedCategories = resolvedCategoryAssignments.length
        ? resolvedCategoryAssignments.map((assignment) => assignment.main_category)
        : [...(categories || [])];

    const payload = {
        product_code: computedProduct?.productCode || priceSource.productCode,
        product_name: form.productName,
        measurement_unit: form.measurementUnit,
        price: parseNumericInput(priceSource.listPriceRonIncVatFinal),
        categories: resolvedCategories,
        category_assignments: resolvedCategoryAssignments,
        price_list_ids: priceListIds.map((priceList) => ({
            id: priceList.id ? priceList.id : priceList,
        })),
        visibleInOfferApp: form.visibleInOfferApp,
        status: (form.status && String(form.status).trim()) || null,
        pricing_detail: builderFormToPricingDetailPayload(
            form,
            settings,
            priceSource,
            priceSource,
        ),
        pricing_settings: { ...settings },
    };

    if (oldProductCode) {
        payload.old_product_code = oldProductCode;
    }

    return payload;
}
